import React from 'react';
import { useField } from 'informed';

import { PhoneNumberField } from '../../PhoneNumberField';

import { FieldContainer } from './styledComponents';

export interface InformedPhoneNumberFieldProps {
  label: string;
  field: string;
  error: string;
  isDisabled: boolean;
  validationSchemaObject: any;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  intlPhoneNumberFieldStyles?: string;
  phoneNumberInputStyles?: string;
  intlPhoneNumberContainerStyles?: string;
  placeholder?: string;
  helperText?: string;
  fieldValidations?: any;
  countryCodeField?: string;
  dialCodeField?: string;
  validateOnBlur?: boolean;
  labelStyles?: string;
  inputFieldStyles?: string;
  isMarginBottomZero?: boolean;
  handleFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  phoneFieldContainerClassName?: string;
  phoneFieldCountryBtnClassName?: string;
  phoneFieldCountryTextClassName?: string;
  phoneFieldErrorTextClassName?: string;
  phoneFieldHelperTextClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  rightElement?: React.ReactNode;
  rightButtonText?: string;
  rightButtonAction?: () => void;
  externalButtonText?: string;
  externalButtonAction?: () => void;
  externalButtonClassName?: string;
  externalButtonElement?: React.ReactNode;
  showErrorWhenFocused?: boolean;
}

const InformedPhoneNumberField = (props: InformedPhoneNumberFieldProps) => {
  const {
    label,
    field,
    error,
    isDisabled,
    isHidden,
    fieldWrapperStyles,
    intlPhoneNumberFieldStyles,
    phoneNumberInputStyles,
    intlPhoneNumberContainerStyles,
    placeholder,
    helperText,
    validationSchemaObject,
    fieldValidations = {},
    countryCodeField = 'countryCode',
    dialCodeField = 'dialCode',
    labelStyles,
    validateOnBlur = true,
    inputFieldStyles,
    isMarginBottomZero,
    handleFocus,
    handleBlur,
    className,
    phoneFieldContainerClassName,
    phoneFieldCountryBtnClassName,
    phoneFieldCountryTextClassName,
    phoneFieldErrorTextClassName,
    phoneFieldHelperTextClassName,
    labelClassName,
    inputClassName,
    rightElement,
    rightButtonText,
    rightButtonAction,
    externalButtonText,
    externalButtonAction,
    externalButtonClassName,
    externalButtonElement,
    showErrorWhenFocused = false
  } = props;

  // Dial code field (registered for submit/validation; UI is India-fixed)
  const { fieldState: dialCodeFieldState } = useField({
    field: dialCodeField,
    validateOnBlur: true,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[dialCodeField],
    error
  });

  const { fieldState: countryCodeFieldState } = useField({
    field: countryCodeField,
    validateOnBlur: true,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[countryCodeField],
    error
  });

  // Main phone number field
  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[field],
    error,
    isDisabled,
    label,
    dialCode: dialCodeFieldState.value,
    countryCode: countryCodeFieldState.value,
    ...fieldValidations
  });

  const { value: phoneNumber } = fieldState;
  const { value: countryCode } = countryCodeFieldState;

  const { onBlur, onChange } = informed;
  const { setValue } = fieldApi;

  const formatPhoneNumber = (phoneNumber: string) => {
    const countryCodePattern = /^(?:\+91|0)/;
    const hasCountryCode = countryCodePattern.test(phoneNumber);
    let cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
    if (
      cleanedPhoneNumber &&
      hasCountryCode &&
      cleanedPhoneNumber.length > 10
    ) {
      cleanedPhoneNumber = cleanedPhoneNumber.slice(-10);
    }
    setValue(cleanedPhoneNumber);
    onChange(cleanedPhoneNumber);
  };

  const handlePhoneNumberChange = (value: string) => {
    formatPhoneNumber(value);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur(e);
    if (handleBlur) {
      handleBlur(e);
    }
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (handleFocus) {
      handleFocus(e);
    }
  };

  const renderPhoneNumberField = () => {
    return (
      <PhoneNumberField
        name={field}
        label={label}
        phoneNumber={phoneNumber as string}
        countryCode={countryCode as string}
        onChangePhoneNumber={handlePhoneNumberChange}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        error={error}
        helperText={helperText}
        isDisabled={isDisabled}
        isRequired={false}
        placeholder={placeholder}
        className={intlPhoneNumberFieldStyles || className}
        labelClassName={labelStyles || labelClassName}
        inputClassName={
          phoneNumberInputStyles || inputFieldStyles || inputClassName
        }
        phoneFieldContainerClassName={
          intlPhoneNumberContainerStyles || phoneFieldContainerClassName
        }
        phoneFieldCountryBtnClassName={phoneFieldCountryBtnClassName}
        phoneFieldCountryTextClassName={phoneFieldCountryTextClassName}
        phoneFieldErrorTextClassName={phoneFieldErrorTextClassName}
        phoneFieldHelperTextClassName={phoneFieldHelperTextClassName}
        rightElement={rightElement}
        rightButtonText={rightButtonText}
        rightButtonAction={rightButtonAction}
        externalButtonText={externalButtonText}
        externalButtonAction={externalButtonAction}
        externalButtonClassName={externalButtonClassName}
        externalButtonElement={externalButtonElement}
        showErrorWhenFocused={showErrorWhenFocused}
      />
    );
  };

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderPhoneNumberField()}
    </FieldContainer>
  );
};

InformedPhoneNumberField.displayName = 'InformedPhoneNumberField';

export { InformedPhoneNumberField };
